import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Menu, MenuItem, Typography } from '@mui/material';
import Loader from 'components/Loader';
import React from 'react';

import { ManifestDialogUrlState } from './dialogState';
import { SlideSelectionTable, SlideSelectionTableRow } from './SlideSelectionTable';

interface CaseManifestSourcePanelProps {
  addButtonText: string;
  assignmentMenuAnchor: HTMLElement | null;
  assignmentStrategy: ManifestDialogUrlState['sourceAssignmentStrategy'];
  canIncludeAssigned: boolean;
  canReadSource: boolean;
  description: string;
  disabled: boolean;
  hasAssignedError: boolean;
  hasPendingError: boolean;
  includeAssigned: boolean;
  isLoading: boolean;
  loadingText: string;
  onAdd: () => void;
  onAssignmentMenuAnchorChange: (anchor: HTMLElement | null) => void;
  onAssignmentStrategyChange: (strategy: ManifestDialogUrlState['sourceAssignmentStrategy']) => void;
  onIncludeAssignedChange: (includeAssigned: boolean) => void;
  onRetry: () => void;
  onSelectedSlideIdsChange: (slideIds: string[]) => void;
  rows: SlideSelectionTableRow[];
  selectedSlideCount: number;
  selectedSlideIds: string[];
  selectionMessage: string | null;
  selectionScopeKey: string;
  sourceName: string;
  sourceUnavailable: boolean;
}

export const CaseManifestSourcePanel = ({
  addButtonText,
  assignmentMenuAnchor,
  assignmentStrategy,
  canIncludeAssigned,
  canReadSource,
  description,
  disabled,
  hasAssignedError,
  hasPendingError,
  includeAssigned,
  isLoading,
  loadingText,
  onAdd,
  onAssignmentMenuAnchorChange,
  onAssignmentStrategyChange,
  onIncludeAssignedChange,
  onRetry,
  onSelectedSlideIdsChange,
  rows,
  selectedSlideCount,
  selectedSlideIds,
  selectionMessage,
  selectionScopeKey,
  sourceName,
  sourceUnavailable,
}: CaseManifestSourcePanelProps) => {
  const groupSelectedSlides = assignmentStrategy === 'groupIntoOneCase';
  const assignmentDisabled = disabled || isLoading || sourceUnavailable || !canIncludeAssigned;
  const chooseAssignment = (strategy: ManifestDialogUrlState['sourceAssignmentStrategy']) => {
    onAssignmentStrategyChange(strategy);
    onAssignmentMenuAnchorChange(null);
  };

  return (
    <Box my={1}>
      <Grid container justifyContent="space-between" alignItems="center" mb={1} rowGap={1}>
        <Grid item>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Grid>
        <Grid item>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              aria-label="Choose assignment"
              aria-haspopup="menu"
              aria-expanded={Boolean(assignmentMenuAnchor)}
              variant="outlined"
              color="inherit"
              endIcon={<ArrowDropDownIcon />}
              onClick={(event) => onAssignmentMenuAnchorChange(event.currentTarget)}
              disabled={assignmentDisabled}
            >
              {assignmentStrategy === 'addToExistingCase'
                ? 'Add to existing case'
                : groupSelectedSlides
                ? 'One case'
                : 'Individual cases'}
            </Button>
            <Menu
              anchorEl={assignmentMenuAnchor}
              open={Boolean(assignmentMenuAnchor)}
              onClose={() => onAssignmentMenuAnchorChange(null)}
            >
              <MenuItem
                selected={assignmentStrategy === 'oneCasePerSlide'}
                onClick={() => chooseAssignment('oneCasePerSlide')}
              >
                Individual cases
              </MenuItem>
              <MenuItem selected={groupSelectedSlides} onClick={() => chooseAssignment('groupIntoOneCase')}>
                One case
              </MenuItem>
              <MenuItem
                selected={assignmentStrategy === 'addToExistingCase'}
                onClick={() => chooseAssignment('addToExistingCase')}
              >
                Add to existing case
              </MenuItem>
            </Menu>
            <Button
              variant="contained"
              color="primary"
              onClick={onAdd}
              disabled={assignmentDisabled || selectedSlideCount === 0}
            >
              {addButtonText}
            </Button>
          </Box>
        </Grid>
      </Grid>
      {canIncludeAssigned && canReadSource && (
        <FormControlLabel
          control={
            <Checkbox checked={includeAssigned} onChange={(_event, checked) => onIncludeAssignedChange(checked)} />
          }
          label="Include assigned slides"
        />
      )}
      {selectionMessage && <Alert severity="warning">{selectionMessage}</Alert>}
      {!includeAssigned && hasPendingError && (
        <Alert severity="error">Could not load {sourceName}. Your case plan is unchanged.</Alert>
      )}
      {includeAssigned && hasAssignedError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not load slide assignments. Your case plan is unchanged. Retry or return to {sourceName}.
        </Alert>
      )}
      {isLoading && (
        <Box mb={1.5}>
          <Loader inline message={loadingText} testId="pending-slides-loading-indicator" />
        </Box>
      )}
      <SlideSelectionTable
        isLoading={isLoading || sourceUnavailable}
        onSelectedSlideIdsChange={onSelectedSlideIdsChange}
        selectionScopeKey={selectionScopeKey}
        rows={rows}
        selectedSlideIds={selectedSlideIds}
      />
    </Box>
  );
};
